import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect } from 'react';

import { ArrowCircleLeft } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { PaymentElement } from '@stripe/react-stripe-js';
import type { Appearance, ConfirmPaymentData } from '@stripe/stripe-js';
import type { UseMutationResult } from '@tanstack/react-query';
import { useLocation } from '@tanstack/react-router';
import toast from 'react-hot-toast';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import type { CloudConnectPaymentMethodSetup } from 'src/api/endpoints/cloudConnect.types';
import {
  useApplyCloudConnectPaymentMethod,
  useSetupCloudConnectPaymentMethod,
} from 'src/api/queries';
import {
  Button,
  Link,
  Modal,
  NotificationBox,
  Spinner,
  WithStripeElements,
} from 'src/components';
import { Link2 } from 'src/components/Link2';
import { LinkExternal } from 'src/components/LinkExternal';
import {
  CustomStripeError,
  StripeIntentType,
  useStripe,
} from 'src/utils/stripe';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

const getStripeError = (
  checkout: UseMutationResult<unknown, Error, ConfirmPaymentData, unknown>,
  componentLocation: string | undefined
) => {
  const { error } = checkout;
  if (error === null) {
    return null;
  }

  if (error instanceof CustomStripeError) {
    if (error.data.type === 'validation_error') {
      return null;
    }

    if (
      error.data.type === 'invalid_request_error' &&
      error.data.code === 'parameter_invalid_empty'
    ) {
      return {
        title: <Trans>Unable to update payment method</Trans>,
        message: (
          <Trans>
            Some of your payment details are missing. Please
            <Link2
              inline
              to="/subscriptions/payment"
              search={{ openBillingInfo: true }}
              reloadDocument={true}
              componentLocation={componentLocation}
            >
              enter your name and country
            </Link2>
            , or{' '}
            <LinkExternal
              inline
              to={SUPPORT_URL.HOME}
              target="_blank"
              rel="noreferrer"
              componentLocation={componentLocation}
            >
              contact support
            </LinkExternal>{' '}
            if the problem persists. We are sorry about the inconvenience.
          </Trans>
        ),
      };
    }

    if (error.data.type === 'card_error' && error.data.message) {
      return {
        title: <Trans>Unable to update payment method</Trans>,
        message: error.data.message,
      };
    }
  }

  return {
    title: <Trans>Unable to update payment method</Trans>,
    message: (
      <Trans>
        Please try again, or{' '}
        <LinkExternal
          inline
          to={SUPPORT_URL.HOME}
          target="_blank"
          rel="noreferrer"
          componentLocation={componentLocation}
        >
          contact support
        </LinkExternal>{' '}
        if the problem persists. We are sorry about the inconvenience.
      </Trans>
    ),
  };
};

// Stripe renders PaymentElement fields outside our normal app CSS, so visual
// styling must be passed through Stripe's Appearance API.
const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    fontFamily: 'reMarkableSans, sans-serif',
    fontSizeBase: '1rem',
    spacingGridRow: '1rem',
    spacingGridColumn: '1rem',
    fontSizeSm: '0.875rem',
    borderRadius: '4px',
  },
  rules: {
    '.Input': {
      height: '48px',
      padding: '16px',
      border: '1px solid #D2CABC',
      boxShadow: 'none',
      lineHeight: '16px',
    },
    '.Input:focus': {
      outline: '2px solid initial inset',
      boxShadow: 'none',
    },
    '.Input--invalid': {
      boxShadow: '0 0 0 2px var(--colorDanger) inset',
    },
    '.Label': {
      color: 'rgb(34, 34, 34)',
      fontWeight: '600',
    },
    '.Error': {
      color: 'rgb(237, 27, 35)',
    },
  },
};

const CloudConnectEditPaymentMethod = ({
  setupIntent,
  componentLocation,
}: {
  setupIntent: CloudConnectPaymentMethodSetup;
  componentLocation: string | undefined;
}) => {
  const { paymentElementProps, checkout } = useStripe(
    StripeIntentType.Setup,
    appearance
  );
  const applyPaymentMethod = useApplyCloudConnectPaymentMethod();
  const location = useLocation();
  const stripeError = getStripeError(checkout, componentLocation);

  const onSubmit = () => {
    checkout.mutate(
      {
        return_url: `${window.location.origin}${location.pathname}`,
        payment_method_data: {
          billing_details: {
            name: setupIntent.name,
            email: setupIntent.email,
            address: {
              line1: setupIntent.address.line1 ?? '',
              line2: setupIntent.address.line2 ?? '',
              city: setupIntent.address.city ?? '',
              state: setupIntent.address.state ?? '',
              country: setupIntent.address.country,
              postal_code: setupIntent.address.postalCode ?? '',
            },
            phone: setupIntent.phone,
          },
        },
      },
      {
        onSuccess: () => {
          applyPaymentMethod.mutate(
            { setupIntentId: setupIntent.setupIntentId },
            { onSuccess: () => toast.success('Payment method updated') }
          );
        },
      }
    );
  };

  return (
    <>
      {stripeError !== null && (
        <NotificationBox
          data-cy="edit-payment-method-stripe-error"
          title={stripeError.title}
          variant="warning"
          className="my-16"
        >
          <Typography variant="body-sm" as="p">
            {stripeError.message}
          </Typography>
        </NotificationBox>
      )}

      <PaymentElement {...paymentElementProps} />

      <Typography variant="body-md" as="p" className="my-16 text-left">
        <Trans>
          You hereby authorize reMarkable to charge you automatically each month
          until you cancel your subscription. See full terms and conditions{' '}
          <Link
            to={SUPPORT_URL.LEGAL_OVERVIEW}
            inline
            trackingLocation={componentLocation}
          >
            here
          </Link>
          .
        </Trans>
      </Typography>

      <div className="mt-24 flex flex-col flex-wrap justify-center gap-16 xs:flex-row">
        <Button
          type="submit"
          data-cy="confirm-purchase-button"
          disabled={checkout.isPending || applyPaymentMethod.isPending}
          loading={checkout.isPending || applyPaymentMethod.isPending}
          onClick={onSubmit}
          className="w-full xs:w-fit"
          analytics={{
            text: 'Confirm changes',
            location: ComponentLocations.MANAGE_CONNECT.MAIN,
            action: 'confirm edit payment method changes',
          }}
        >
          <Trans>Confirm changes</Trans>
        </Button>

        <Modal.Close asChild>
          <Button
            variant="secondary"
            disabled={checkout.isPending || applyPaymentMethod.isPending}
            className="w-full xs:w-fit"
            analytics={{
              text: 'Cancel',
              location: ComponentLocations.MANAGE_CONNECT.MAIN,
              action: 'cancel edit payment method changes',
            }}
          >
            <Trans>Cancel</Trans>
          </Button>
        </Modal.Close>
      </div>
    </>
  );
};

const CloudConnectEditPaymentMethodWrapper = ({
  componentLocation,
}: {
  componentLocation: string;
}) => {
  const { t } = useLingui();
  const setupPaymentMethod = useSetupCloudConnectPaymentMethod();

  useEffect(() => {
    if (setupPaymentMethod.isIdle) {
      setupPaymentMethod.mutate();
    }
  }, [setupPaymentMethod]);

  if (setupPaymentMethod.isError) {
    return (
      <>
        <NotificationBox
          title={t`Something went wrong`}
          data-cy="edit-payment-method-create-setup-intent-failed"
          error={setupPaymentMethod.error}
        >
          <Typography variant="body-sm" as="p">
            <Trans>
              Please try again, or{' '}
              <a href={SUPPORT_URL.HOME} target="_blank" rel="noreferrer">
                contact support
              </a>{' '}
              if the problem persists. We are sorry about the inconvenience.
            </Trans>
          </Typography>
        </NotificationBox>

        <Modal.Close asChild>
          <Button
            variant="secondary"
            analytics={{
              text: 'Back',
              location: ComponentLocations.MANAGE_CONNECT.MAIN,
              action: 'close edit payment method modal',
            }}
          >
            <ArrowCircleLeft />
            <span>
              <Trans>Back</Trans>
            </span>
          </Button>
        </Modal.Close>
      </>
    );
  }

  if (setupPaymentMethod.isPending || !setupPaymentMethod.data?.clientSecret) {
    return <Spinner />;
  }

  return (
    <WithStripeElements
      options={{ clientSecret: setupPaymentMethod.data.clientSecret }}
      publishableKey={setupPaymentMethod.data.publishableKey}
    >
      <CloudConnectEditPaymentMethod
        setupIntent={setupPaymentMethod.data}
        componentLocation={componentLocation}
      />
    </WithStripeElements>
  );
};

export const CloudConnectEditPaymentMethodModal = ({
  componentLocation,
}: {
  componentLocation: string;
}) => {
  const { t } = useLingui();

  return (
    <Modal.Content title={t`Change payment method`}>
      <CloudConnectEditPaymentMethodWrapper
        componentLocation={componentLocation}
      />
    </Modal.Content>
  );
};
